// src/pages/CRM/SFA/QuoteBuilder.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Grid, TextField, Button, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Autocomplete, InputAdornment, Divider, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, LinearProgress, Chip,
  FormControlLabel, Switch, Tooltip, Stack, Avatar, useTheme
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Send as SendIcon,
  Description as QuoteIcon,
  ShoppingCart as ProductIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  LocalShipping as ShippingIcon,
  Warning as WarningIcon,
  Inventory2 as BundleIcon,
  SubdirectoryArrowRight as SubItemIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
  formatCurrency,
  QuoteStatus,
  QuoteStatusLabels,
  QuoteStatusColors,
  isQuoteEditable,
  isQuoteSendable,
  requiresApproval,
  canApproveQuote,
  ProductType
} from './sfaEnums';
import { quoteService, productService, opportunityService, priceBookService, sfaSettingsService } from './sfaService';

const QuoteBuilder = () => {
  const { chatbotId, quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const isEdit = Boolean(quoteId) && quoteId !== 'new';
  const opportunityIdFromUrl = searchParams.get('opportunity_id');

  // Permissions
  const [canModify, setCanModify] = useState(false);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Quote data
  const [quote, setQuote] = useState({
    name: '',
    opportunity_id: opportunityIdFromUrl || null,
    account_id: null,
    contact_id: null,
    price_book_id: null,
    expiration_date: null,
    status: QuoteStatus.DRAFT,
    additional_discount_type: 'amount',
    additional_discount_value: 0,
    shipping_amount: 0,
    payment_terms: 'Net 30',
    shipping_terms: '',
    terms_and_conditions: '',
    billing_name: '',
    billing_street: '',
    billing_city: '',
    billing_state: '',
    billing_postal_code: '',
    billing_country: '',
    shipping_name: '',
    shipping_street: '',
    shipping_city: '',
    shipping_state: '',
    shipping_postal_code: '',
    shipping_country: ''
  });

  const [lineItems, setLineItems] = useState([]);

  // Lookup data
  const [opportunities, setOpportunities] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [products, setProducts] = useState([]);
  const [priceBooks, setPriceBooks] = useState([]);

  // Selected lookups
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedPriceBook, setSelectedPriceBook] = useState(null);

  // Product dialog
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productQuantity, setProductQuantity] = useState(1);
  const [productUnitPrice, setProductUnitPrice] = useState(0);
  const [productDiscountType, setProductDiscountType] = useState('percent');
  const [productDiscountValue, setProductDiscountValue] = useState(0);

  // Copy address toggle
  const [copyBillingToShipping, setCopyBillingToShipping] = useState(false);

  // SFA Settings (for discount approval threshold)
  const [sfaSettings, setSfaSettings] = useState({
    discount_approval_enabled: true,
    discount_approval_threshold: 20.0
  });

  // Check permissions
  useEffect(() => {
    const checkPerms = async () => {
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanModify(modifyPerm);
    };
    checkPerms();
  }, []);

  // Calculate totals
  const totals = useMemo(() => {
    const subtotal = lineItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
    const taxAmount = lineItems.reduce((sum, item) => sum + (item.tax_amount || 0), 0);

    let discountAmount = 0;
    if (quote.additional_discount_type === 'percent') {
      discountAmount = subtotal * ((parseFloat(quote.additional_discount_value) || 0) / 100);
    } else {
      discountAmount = parseFloat(quote.additional_discount_value) || 0;
    }

    const shippingAmount = parseFloat(quote.shipping_amount) || 0;
    const total = subtotal - discountAmount + taxAmount + shippingAmount;

    return { subtotal, discountAmount, taxAmount, shippingAmount, total };
  }, [lineItems, quote.additional_discount_type, quote.additional_discount_value, quote.shipping_amount]);

  // Calculate discount analysis for approval workflow
  const discountAnalysis = useMemo(() => {
    // Calculate total list price (before any discounts)
    const totalListPrice = lineItems.reduce((sum, item) => {
      const listPrice = item.list_price || item.unit_price || 0;
      return sum + (listPrice * item.quantity);
    }, 0);

    // Calculate total line item discounts
    const lineItemDiscountTotal = lineItems.reduce((sum, item) => sum + (item.discount_amount || 0), 0);

    // Additional quote-level discount
    const additionalDiscount = totals.discountAmount;

    // Total discount amount
    const totalDiscountAmount = lineItemDiscountTotal + additionalDiscount;

    // Calculate overall discount percentage
    const totalDiscountPercent = totalListPrice > 0
      ? (totalDiscountAmount / totalListPrice) * 100
      : 0;

    // Check if approval is required
    const exceedsThreshold = sfaSettings.discount_approval_enabled &&
      totalDiscountPercent > sfaSettings.discount_approval_threshold;

    return {
      totalListPrice,
      lineItemDiscountTotal,
      additionalDiscount,
      totalDiscountAmount,
      totalDiscountPercent: Math.round(totalDiscountPercent * 100) / 100,
      exceedsThreshold,
      threshold: sfaSettings.discount_approval_threshold
    };
  }, [lineItems, totals.discountAmount, sfaSettings]);

  // Fetch quote data
  const fetchQuote = useCallback(async () => {
    if (!isEdit) return;

    setLoading(true);
    try {
      const response = await quoteService.getById(quoteId);
      if (response.success) {
        const quoteData = response.data;

        setQuote({
          name: quoteData.name || '',
          opportunity_id: quoteData.opportunity_id,
          account_id: quoteData.account_id,
          contact_id: quoteData.contact_id,
          price_book_id: quoteData.price_book_id,
          expiration_date: quoteData.expiration_date ? new Date(quoteData.expiration_date) : null,
          status: quoteData.status || QuoteStatus.DRAFT,
          additional_discount_type: quoteData.additional_discount_type || 'amount',
          additional_discount_value: quoteData.additional_discount_value || 0,
          shipping_amount: quoteData.shipping_amount || 0,
          payment_terms: quoteData.payment_terms || 'Net 30',
          shipping_terms: quoteData.shipping_terms || '',
          terms_and_conditions: quoteData.terms_and_conditions || '',
          billing_name: quoteData.billing_address?.name || '',
          billing_street: quoteData.billing_address?.street || '',
          billing_city: quoteData.billing_address?.city || '',
          billing_state: quoteData.billing_address?.state || '',
          billing_postal_code: quoteData.billing_address?.postal_code || '',
          billing_country: quoteData.billing_address?.country || '',
          shipping_name: quoteData.shipping_address?.name || '',
          shipping_street: quoteData.shipping_address?.street || '',
          shipping_city: quoteData.shipping_address?.city || '',
          shipping_state: quoteData.shipping_address?.state || '',
          shipping_postal_code: quoteData.shipping_address?.postal_code || '',
          shipping_country: quoteData.shipping_address?.country || ''
        });

        setLineItems(quoteData.line_items || []);

        if (quoteData.opportunity) {
          setSelectedOpportunity(quoteData.opportunity);
        }
        if (quoteData.account) {
          setSelectedAccount(quoteData.account);
        }
        if (quoteData.contact) {
          setSelectedContact(quoteData.contact);
        }
        if (quoteData.price_book) {
          setSelectedPriceBook(quoteData.price_book);
        }
      }
    } catch (error) {
      console.error('Error fetching quote:', error);
      enqueueSnackbar('Failed to load quote', { variant: 'error' });
      navigate(`/chatbot/${chatbotId}/crm/quotes`);
    } finally {
      setLoading(false);
    }
  }, [quoteId, isEdit, chatbotId, navigate, enqueueSnackbar]);

  // Fetch lookup data
  const fetchLookupData = async () => {
    try {
      const [oppsRes, accountsRes, productsRes, priceBooksRes, settingsRes] = await Promise.all([
        opportunityService.getAll({ chatbot_id: chatbotId, per_page: 100 }),
        axiosInstance.get('/api/crm/accounts', { params: { chatbot_id: chatbotId, per_page: 100 } }),
        productService.getAll({ status: 'ACTIVE', per_page: 100 }),
        axiosInstance.get('/api/crm/price-books', { params: { is_active: true } }),
        sfaSettingsService.get().catch(() => ({ data: null })) // Gracefully handle if settings don't exist
      ]);

      setOpportunities(oppsRes.data || []);
      setAccounts(accountsRes.data?.data || []);
      setProducts(productsRes.data || []);
      setPriceBooks(priceBooksRes.data?.data || []);

      // Set SFA settings if available
      if (settingsRes?.data) {
        setSfaSettings({
          discount_approval_enabled: settingsRes.data.discount_approval_enabled ?? true,
          discount_approval_threshold: settingsRes.data.discount_approval_threshold ?? 20.0
        });
      }

      // If opportunity ID was passed, load opportunity details
      if (opportunityIdFromUrl && !isEdit) {
        const opp = oppsRes.data?.find(o => o.id === parseInt(opportunityIdFromUrl));
        if (opp) {
          setSelectedOpportunity(opp);
          if (opp.account) {
            setSelectedAccount(opp.account);
            setQuote(prev => ({ ...prev, account_id: opp.account.id }));
          }
          if (opp.contact) {
            setSelectedContact(opp.contact);
            setQuote(prev => ({ ...prev, contact_id: opp.contact.id }));
          }
          setQuote(prev => ({
            ...prev,
            name: `Quote for ${opp.name}`,
            opportunity_id: opp.id
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching lookup data:', error);
    }
  };

  const fetchContacts = async (accountId) => {
    if (!accountId) {
      setContacts([]);
      return;
    }
    try {
      const response = await axiosInstance.get('/api/crm/contacts', {
        params: { account_id: accountId, per_page: 100 }
      });
      setContacts(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  useEffect(() => {
    fetchLookupData();
    if (isEdit) {
      fetchQuote();
    }
  }, [isEdit, fetchQuote]);

  useEffect(() => {
    if (selectedAccount) {
      fetchContacts(selectedAccount.id);
    }
  }, [selectedAccount]);

  // Copy billing to shipping
  useEffect(() => {
    if (copyBillingToShipping) {
      // Immediately copy current billing values to shipping
      setQuote(prev => {
        const updates = {
          shipping_name: prev.billing_name || '',
          shipping_street: prev.billing_street || '',
          shipping_city: prev.billing_city || '',
          shipping_state: prev.billing_state || '',
          shipping_postal_code: prev.billing_postal_code || '',
          shipping_country: prev.billing_country || ''
        };
        
        // Only update if values actually changed to prevent infinite loops
        const hasChanges = 
          prev.shipping_name !== updates.shipping_name ||
          prev.shipping_street !== updates.shipping_street ||
          prev.shipping_city !== updates.shipping_city ||
          prev.shipping_state !== updates.shipping_state ||
          prev.shipping_postal_code !== updates.shipping_postal_code ||
          prev.shipping_country !== updates.shipping_country;
        
        if (hasChanges) {
          return { ...prev, ...updates };
        }
        return prev;
      });
    }
  }, [
    copyBillingToShipping, 
    quote.billing_name, 
    quote.billing_street, 
    quote.billing_city, 
    quote.billing_state, 
    quote.billing_postal_code, 
    quote.billing_country
  ]);

  // Handlers
  const handleQuoteChange = (field, value) => {
    setQuote(prev => ({ ...prev, [field]: value }));
  };

  const handleOpportunityChange = (event, newValue) => {
    setSelectedOpportunity(newValue);
    handleQuoteChange('opportunity_id', newValue?.id || null);

    if (newValue) {
      if (newValue.account) {
        setSelectedAccount(newValue.account);
        handleQuoteChange('account_id', newValue.account.id);
      }
      if (newValue.contact) {
        setSelectedContact(newValue.contact);
        handleQuoteChange('contact_id', newValue.contact.id);
      }
      if (!quote.name) {
        handleQuoteChange('name', `Quote for ${newValue.name}`);
      }
    }
  };

  const handleAccountChange = (event, newValue) => {
    setSelectedAccount(newValue);
    handleQuoteChange('account_id', newValue?.id || null);
    setSelectedContact(null);
    handleQuoteChange('contact_id', null);

    if (newValue) {
      // Auto-fill billing address from account
      handleQuoteChange('billing_name', newValue.name);
      handleQuoteChange('billing_street', newValue.billing_street || '');
      handleQuoteChange('billing_city', newValue.billing_city || '');
      handleQuoteChange('billing_state', newValue.billing_state || '');
      handleQuoteChange('billing_postal_code', newValue.billing_postal_code || '');
      handleQuoteChange('billing_country', newValue.billing_country || '');
    }
  };

  const handleContactChange = (event, newValue) => {
    setSelectedContact(newValue);
    handleQuoteChange('contact_id', newValue?.id || null);
  };

  const calculateLineItemTotal = (quantity, unitPrice, discountType, discountValue, taxRate = 0) => {
    const subtotal = quantity * unitPrice;
    let discountAmount = 0;
    if (discountType === 'percent') {
      discountAmount = subtotal * ((discountValue || 0) / 100);
    } else {
      discountAmount = discountValue || 0;
    }
    const totalBeforeTax = subtotal - discountAmount;
    const taxAmount = totalBeforeTax * ((taxRate || 0) / 100);
    return {
      subtotal,
      discount_amount: discountAmount,
      total_price: totalBeforeTax,
      tax_amount: taxAmount
    };
  };

  const handleAddProduct = async () => {
    if (!selectedProduct) return;

    // Check if the product is a bundle
    if (selectedProduct.product_type === ProductType.BUNDLE) {
      try {
        // Fetch bundle details
        const bundleResponse = await productService.getBundleDetails(selectedProduct.id);
        const bundleItems = bundleResponse.data?.bundle_items || [];

        if (bundleItems.length === 0) {
          enqueueSnackbar('This bundle has no components configured', { variant: 'warning' });
          return;
        }

        const bundleParentId = `bundle-${Date.now()}`;
        const newLineItems = [];

        // Add bundle header line item
        const headerCalcs = calculateLineItemTotal(
          productQuantity,
          productUnitPrice || selectedProduct.standard_price,
          productDiscountType,
          productDiscountValue
        );

        newLineItems.push({
          id: bundleParentId,
          product_id: selectedProduct.id,
          product: selectedProduct,
          name: selectedProduct.name,
          sku: selectedProduct.sku,
          quantity: productQuantity,
          unit_price: productUnitPrice || selectedProduct.standard_price || 0,
          list_price: selectedProduct.standard_price || 0,
          discount_type: productDiscountType,
          discount_value: productDiscountValue,
          ...headerCalcs,
          tax_rate: 0,
          sort_order: lineItems.length,
          is_bundle_header: true,
          bundle_product_id: selectedProduct.id
        });

        // Add bundle component line items
        bundleItems.forEach((bundleItem, index) => {
          const childProduct = bundleItem.child_product;
          const childQuantity = (bundleItem.quantity || 1) * productQuantity;
          const childUnitPrice = bundleItem.override_price ?? childProduct.standard_price ?? 0;
          const childDiscountValue = bundleItem.discount_percent || 0;

          const childCalcs = calculateLineItemTotal(
            childQuantity,
            childUnitPrice,
            'percent',
            childDiscountValue
          );

          newLineItems.push({
            id: `temp-${Date.now()}-${index}`,
            product_id: childProduct.id,
            product: childProduct,
            name: childProduct.name,
            sku: childProduct.sku,
            quantity: childQuantity,
            unit_price: childUnitPrice,
            list_price: childProduct.standard_price || 0,
            discount_type: 'percent',
            discount_value: childDiscountValue,
            ...childCalcs,
            tax_rate: 0,
            sort_order: lineItems.length + index + 1,
            is_bundle_component: true,
            bundle_parent_id: bundleParentId,
            bundle_product_id: selectedProduct.id,
            is_optional: bundleItem.is_optional
          });
        });

        setLineItems(prev => [...prev, ...newLineItems]);
        enqueueSnackbar(`Bundle "${selectedProduct.name}" added with ${bundleItems.length} components`, { variant: 'success' });
      } catch (error) {
        console.error('Error fetching bundle details:', error);
        enqueueSnackbar('Failed to load bundle components', { variant: 'error' });
      }
    } else {
      // Regular product - add single line item
      const calcs = calculateLineItemTotal(
        productQuantity,
        productUnitPrice || selectedProduct.standard_price,
        productDiscountType,
        productDiscountValue
      );

      const newLineItem = {
        id: `temp-${Date.now()}`,
        product_id: selectedProduct.id,
        product: selectedProduct,
        name: selectedProduct.name,
        sku: selectedProduct.sku,
        quantity: productQuantity,
        unit_price: productUnitPrice || selectedProduct.standard_price || 0,
        list_price: selectedProduct.standard_price || 0,
        discount_type: productDiscountType,
        discount_value: productDiscountValue,
        ...calcs,
        tax_rate: 0,
        sort_order: lineItems.length
      };

      setLineItems(prev => [...prev, newLineItem]);
    }

    setProductDialogOpen(false);
    resetProductDialog();
  };

  const resetProductDialog = () => {
    setSelectedProduct(null);
    setProductQuantity(1);
    setProductUnitPrice(0);
    setProductDiscountType('percent');
    setProductDiscountValue(0);
  };

  const handleRemoveLineItem = (index) => {
    const itemToRemove = lineItems[index];

    // If it's a bundle header, remove all its components too
    if (itemToRemove.is_bundle_header) {
      const bundleId = itemToRemove.id;
      setLineItems(prev => prev.filter(item =>
        item.id !== bundleId && item.bundle_parent_id !== bundleId
      ));
      return;
    }

    // Don't allow removing individual bundle components
    if (itemToRemove.is_bundle_component) {
      enqueueSnackbar('Cannot remove individual bundle components. Remove the bundle header instead.', { variant: 'info' });
      return;
    }

    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleLineItemChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Recalculate totals
      const item = updated[index];
      const calcs = calculateLineItemTotal(
        field === 'quantity' ? value : item.quantity,
        field === 'unit_price' ? value : item.unit_price,
        field === 'discount_type' ? value : item.discount_type,
        field === 'discount_value' ? value : item.discount_value,
        field === 'tax_rate' ? value : item.tax_rate
      );
      updated[index] = { ...updated[index], ...calcs };

      return updated;
    });
  };

  const handleSave = async () => {
    if (!quote.name) {
      enqueueSnackbar('Quote name is required', { variant: 'error' });
      return;
    }

    setSaving(true);
    try {
      // Prepare shipping address - ensure it's set if "Same as billing" is enabled
      let shippingData = {
        shipping_name: quote.shipping_name,
        shipping_street: quote.shipping_street,
        shipping_city: quote.shipping_city,
        shipping_state: quote.shipping_state,
        shipping_postal_code: quote.shipping_postal_code,
        shipping_country: quote.shipping_country
      };
      
      // If "Same as billing" is enabled, explicitly copy billing to shipping
      if (copyBillingToShipping) {
        shippingData = {
          shipping_name: quote.billing_name,
          shipping_street: quote.billing_street,
          shipping_city: quote.billing_city,
          shipping_state: quote.billing_state,
          shipping_postal_code: quote.billing_postal_code,
          shipping_country: quote.billing_country
        };
      }

      const payload = {
        ...quote,
        ...shippingData,  // Ensure shipping data is included
        chatbot_id: chatbotId,
        expiration_date: quote.expiration_date ? quote.expiration_date.toISOString().split('T')[0] : null,
        subtotal: totals.subtotal,
        tax_amount: totals.taxAmount,
        discount_amount: totals.discountAmount,
        total_amount: totals.total,
        line_items: lineItems.map((item, index) => ({
          product_id: item.product_id,
          name: item.name,
          sku: item.sku,
          quantity: item.quantity,
          unit_price: item.unit_price,
          list_price: item.list_price,
          discount_type: item.discount_type,
          discount_value: item.discount_value,
          tax_rate: item.tax_rate || 0,
          sort_order: index
        }))
      };

      let response;
      if (isEdit) {
        response = await quoteService.update(quoteId, payload);
        enqueueSnackbar('Quote updated successfully', { variant: 'success' });
      } else {
        response = await quoteService.create(payload);
        enqueueSnackbar('Quote created successfully', { variant: 'success' });
      }

      navigate(`/chatbot/${chatbotId}/crm/quotes`);
    } catch (error) {
      console.error('Error saving quote:', error);
      enqueueSnackbar(error.response?.data?.message || 'Failed to save quote', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };


  const handleSendQuote = async () => {
    if (!isEdit) {
      enqueueSnackbar('Please save the quote first', { variant: 'warning' });
      return;
    }

    try {
      await quoteService.updateStatus(quoteId, { status: 'SENT' });
      enqueueSnackbar('Quote sent successfully', { variant: 'success' });
      fetchQuote();
    } catch (error) {
      enqueueSnackbar('Failed to send quote', { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, mt: 10 }}>
        <LinearProgress />
      </Box>
    );
  }

  const canEdit = !isEdit || isQuoteEditable(quote.status);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3, mt: 10 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              <QuoteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {isEdit ? 'Edit Quote' : 'New Quote'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEdit ? 'Update quote details and line items' : 'Create a new sales quote'}
            </Typography>
          </Box>
          {isEdit && quote.status && (
            <Chip
              label={QuoteStatusLabels[quote.status] || quote.status}
              color={QuoteStatusColors[quote.status] || 'default'}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </Box>

        {!canEdit && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            This quote cannot be edited because it has been {QuoteStatusLabels[quote.status]?.toLowerCase() || 'processed'}.
          </Alert>
        )}

        {/* Discount Approval Warning */}
        {discountAnalysis.exceedsThreshold && (
          <Alert
            severity="warning"
            sx={{ mb: 3, borderRadius: 2 }}
            icon={false}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                label={`${discountAnalysis.totalDiscountPercent}% Discount`}
                color="warning"
                size="small"
              />
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Approval Required
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total discount exceeds the {discountAnalysis.threshold}% threshold.
                  This quote will require approval before it can be sent.
                </Typography>
              </Box>
            </Box>
          </Alert>
        )}

        {/* Quote is pending approval */}
        {quote.status === QuoteStatus.PENDING_APPROVAL && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              Pending Approval
            </Typography>
            <Typography variant="caption">
              This quote is waiting for approval due to discount threshold being exceeded.
              Once approved, it can be sent to the customer.
            </Typography>
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            {/* Quote Info */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Quote Information
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Quote Name"
                    required
                    value={quote.name}
                    onChange={(e) => handleQuoteChange('name', e.target.value)}
                    disabled={!canEdit}
                    placeholder="e.g., Enterprise Software License - Q1 2026"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={opportunities}
                    getOptionLabel={(option) => option.name || ''}
                    value={selectedOpportunity}
                    onChange={handleOpportunityChange}
                    disabled={!canEdit}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Opportunity"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <MoneyIcon color="action" sx={{ ml: 1, mr: 0.5 }} />
                              {params.InputProps.startAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Expiration Date"
                    value={quote.expiration_date}
                    onChange={(date) => handleQuoteChange('expiration_date', date)}
                    disabled={!canEdit}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={accounts}
                    getOptionLabel={(option) => option.name || ''}
                    value={selectedAccount}
                    onChange={handleAccountChange}
                    disabled={!canEdit}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Account"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <BusinessIcon color="action" sx={{ ml: 1, mr: 0.5 }} />
                              {params.InputProps.startAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={contacts}
                    getOptionLabel={(option) => {
                      if (!option) return '';
                      if (option.full_name) return option.full_name;
                      return `${option.first_name || ''} ${option.last_name || ''}`.trim();
                    }}
                    value={selectedContact}
                    onChange={handleContactChange}
                    disabled={!canEdit || !selectedAccount}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Contact"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <PersonIcon color="action" sx={{ ml: 1, mr: 0.5 }} />
                              {params.InputProps.startAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={priceBooks}
                    getOptionLabel={(option) => option.name || ''}
                    value={selectedPriceBook}
                    onChange={(e, v) => {
                      setSelectedPriceBook(v);
                      handleQuoteChange('price_book_id', v?.id || null);
                    }}
                    disabled={!canEdit}
                    renderInput={(params) => (
                      <TextField {...params} label="Price Book" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Payment Terms"
                    value={quote.payment_terms}
                    onChange={(e) => handleQuoteChange('payment_terms', e.target.value)}
                    disabled={!canEdit}
                    placeholder="e.g., Net 30"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Line Items */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  <ProductIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Line Items
                </Typography>
                {canEdit && (
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setProductDialogOpen(true)}>
                    Add Product
                  </Button>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Qty</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Discount</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Tax %</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                      {canEdit && <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItems.map((item, index) => (
                      <TableRow
                        key={item.id || index}
                        hover
                        sx={{
                          ...(item.is_bundle_header && {
                            bgcolor: 'primary.50',
                            '& td': { fontWeight: 600 }
                          }),
                          ...(item.is_bundle_component && {
                            bgcolor: 'grey.50',
                            '& td': { color: 'text.secondary' }
                          })
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: item.is_bundle_component ? 3 : 0 }}>
                            {item.is_bundle_component && (
                              <SubItemIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                            )}
                            <Avatar
                              variant="rounded"
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: item.is_bundle_header ? 'secondary.light' : 'primary.light'
                              }}
                            >
                              {item.is_bundle_header ? (
                                <BundleIcon sx={{ fontSize: 18 }} />
                              ) : (
                                <ProductIcon sx={{ fontSize: 18 }} />
                              )}
                            </Avatar>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontWeight={item.is_bundle_header ? 700 : 600}>
                                  {item.name}
                                </Typography>
                                {item.is_bundle_header && (
                                  <Chip label="Bundle" size="small" color="secondary" sx={{ height: 18, fontSize: 10 }} />
                                )}
                                {item.is_optional && (
                                  <Chip label="Optional" size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                                )}
                              </Box>
                              <Typography variant="caption" color="text.secondary">{item.sku}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {canEdit ? (
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity}
                              onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 1)}
                              sx={{ width: 70 }}
                              inputProps={{ min: 1 }}
                            />
                          ) : item.quantity}
                        </TableCell>
                        <TableCell align="right">
                          {canEdit ? (
                            <TextField
                              type="number"
                              size="small"
                              value={item.unit_price}
                              onChange={(e) => handleLineItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                              sx={{ width: 100 }}
                              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            />
                          ) : formatCurrency(item.unit_price)}
                        </TableCell>
                        <TableCell align="right">
                          {canEdit ? (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <TextField
                                type="number"
                                size="small"
                                value={item.discount_value}
                                onChange={(e) => handleLineItemChange(index, 'discount_value', parseFloat(e.target.value) || 0)}
                                sx={{ width: 70 }}
                              />
                              <Select
                                size="small"
                                value={item.discount_type}
                                onChange={(e) => handleLineItemChange(index, 'discount_type', e.target.value)}
                                sx={{ minWidth: 50 }}
                              >
                                <MenuItem value="percent">%</MenuItem>
                                <MenuItem value="amount">$</MenuItem>
                              </Select>
                            </Stack>
                          ) : (
                            `${item.discount_value}${item.discount_type === 'percent' ? '%' : '$'}`
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {canEdit ? (
                            <TextField
                              type="number"
                              size="small"
                              value={item.tax_rate || 0}
                              onChange={(e) => handleLineItemChange(index, 'tax_rate', parseFloat(e.target.value) || 0)}
                              sx={{ width: 60 }}
                              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                            />
                          ) : `${item.tax_rate || 0}%`}
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={600}>{formatCurrency(item.total_price)}</Typography>
                        </TableCell>
                        {canEdit && (
                          <TableCell align="center">
                            <Tooltip
                              title={item.is_bundle_component ? 'Remove bundle header to delete' : 'Remove item'}
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveLineItem(index)}
                                  disabled={item.is_bundle_component}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {lineItems.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={canEdit ? 7 : 6} align="center" sx={{ py: 6 }}>
                          <ProductIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                          <Typography color="text.secondary">
                            No products added yet. Click "Add Product" to start.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Addresses */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                <ShippingIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Addresses
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={3}>
                {/* Billing Address */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom color="primary" fontWeight={600}>Billing Address</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Name" value={quote.billing_name} onChange={(e) => handleQuoteChange('billing_name', e.target.value)} disabled={!canEdit} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Street" value={quote.billing_street} onChange={(e) => handleQuoteChange('billing_street', e.target.value)} disabled={!canEdit} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="City" value={quote.billing_city} onChange={(e) => handleQuoteChange('billing_city', e.target.value)} disabled={!canEdit} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="State" value={quote.billing_state} onChange={(e) => handleQuoteChange('billing_state', e.target.value)} disabled={!canEdit} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="Postal Code" value={quote.billing_postal_code} onChange={(e) => handleQuoteChange('billing_postal_code', e.target.value)} disabled={!canEdit} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="Country" value={quote.billing_country} onChange={(e) => handleQuoteChange('billing_country', e.target.value)} disabled={!canEdit} />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Shipping Address */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="primary" fontWeight={600}>Shipping Address</Typography>
                    {canEdit && (
                      <FormControlLabel
                        control={<Switch size="small" checked={copyBillingToShipping} onChange={(e) => setCopyBillingToShipping(e.target.checked)} />}
                        label={<Typography variant="caption">Same as billing</Typography>}
                      />
                    )}
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Name" value={quote.shipping_name} onChange={(e) => handleQuoteChange('shipping_name', e.target.value)} disabled={!canEdit || copyBillingToShipping} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Street" value={quote.shipping_street} onChange={(e) => handleQuoteChange('shipping_street', e.target.value)} disabled={!canEdit || copyBillingToShipping} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="City" value={quote.shipping_city} onChange={(e) => handleQuoteChange('shipping_city', e.target.value)} disabled={!canEdit || copyBillingToShipping} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="State" value={quote.shipping_state} onChange={(e) => handleQuoteChange('shipping_state', e.target.value)} disabled={!canEdit || copyBillingToShipping} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="Postal Code" value={quote.shipping_postal_code} onChange={(e) => handleQuoteChange('shipping_postal_code', e.target.value)} disabled={!canEdit || copyBillingToShipping} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="Country" value={quote.shipping_country} onChange={(e) => handleQuoteChange('shipping_country', e.target.value)} disabled={!canEdit || copyBillingToShipping} />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>

            {/* Terms */}
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>Terms & Conditions</Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Shipping Terms"
                    value={quote.shipping_terms}
                    onChange={(e) => handleQuoteChange('shipping_terms', e.target.value)}
                    disabled={!canEdit}
                    placeholder="e.g., FOB Destination"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Terms & Conditions"
                    multiline
                    rows={4}
                    value={quote.terms_and_conditions}
                    onChange={(e) => handleQuoteChange('terms_and_conditions', e.target.value)}
                    disabled={!canEdit}
                    placeholder="Enter any terms and conditions for this quote..."
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Summary Sidebar */}
          <Grid item xs={12} md={4}>
            <Card sx={{ position: 'sticky', top: 80, borderRadius: 2, boxShadow: theme.shadows[2] }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={600}>Quote Summary</Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                    <Typography variant="body2" fontWeight={500}>{formatCurrency(totals.subtotal)}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Additional Discount</Typography>
                    {canEdit ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                          type="number"
                          size="small"
                          value={quote.additional_discount_value}
                          onChange={(e) => handleQuoteChange('additional_discount_value', parseFloat(e.target.value) || 0)}
                          sx={{ width: 80 }}
                        />
                        <Select
                          size="small"
                          value={quote.additional_discount_type}
                          onChange={(e) => handleQuoteChange('additional_discount_type', e.target.value)}
                          sx={{ minWidth: 50 }}
                        >
                          <MenuItem value="percent">%</MenuItem>
                          <MenuItem value="amount">$</MenuItem>
                        </Select>
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="error">-{formatCurrency(totals.discountAmount)}</Typography>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Tax</Typography>
                    <Typography variant="body2">{formatCurrency(totals.taxAmount)}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Shipping</Typography>
                    {canEdit ? (
                      <TextField
                        type="number"
                        size="small"
                        value={quote.shipping_amount}
                        onChange={(e) => handleQuoteChange('shipping_amount', parseFloat(e.target.value) || 0)}
                        sx={{ width: 100 }}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      />
                    ) : (
                      <Typography variant="body2">{formatCurrency(totals.shippingAmount)}</Typography>
                    )}
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight={700}>Total</Typography>
                    <Typography variant="h5" fontWeight={700} color="primary">{formatCurrency(totals.total)}</Typography>
                  </Box>

                  {lineItems.length > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {lineItems.length} line item{lineItems.length !== 1 ? 's' : ''}
                    </Typography>
                  )}
                </Stack>

                <Divider sx={{ my: 3 }} />

                <Stack spacing={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                    sx={{ borderRadius: 2, py: 1.5, boxShadow: theme.shadows[2] }}
                  >
                    {saving ? 'Saving...' : isEdit ? 'Update Quote' : 'Create Quote'}
                  </Button>

                  {isEdit && isQuoteSendable(quote.status) && canEdit && (
                    <Tooltip
                      title={discountAnalysis.exceedsThreshold ? 'Quote requires approval before sending' : ''}
                      placement="top"
                    >
                      <span>
                        <Button
                          fullWidth
                          variant="outlined"
                          size="large"
                          startIcon={<SendIcon />}
                          onClick={handleSendQuote}
                          disabled={discountAnalysis.exceedsThreshold}
                          sx={{ borderRadius: 2, py: 1.5 }}
                        >
                          Send Quote
                        </Button>
                      </span>
                    </Tooltip>
                  )}

                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => navigate(-1)}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Add Product Dialog */}
        <Dialog open={productDialogOpen} onClose={() => { setProductDialogOpen(false); resetProductDialog(); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
          <DialogTitle sx={{ fontWeight: 600 }}>Add Product</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <Autocomplete
                  options={products}
                  getOptionLabel={(option) => `${option.name} (${option.sku || 'No SKU'})`}
                  value={selectedProduct}
                  onChange={(e, newValue) => {
                    setSelectedProduct(newValue);
                    if (newValue) {
                      setProductUnitPrice(newValue.standard_price || 0);
                    }
                  }}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Avatar variant="rounded" sx={{ width: 28, height: 28, bgcolor: option.product_type === ProductType.BUNDLE ? 'secondary.light' : 'primary.light' }}>
                          {option.product_type === ProductType.BUNDLE ? (
                            <BundleIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <ProductIcon sx={{ fontSize: 16 }} />
                          )}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2">{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{option.sku || 'No SKU'}</Typography>
                        </Box>
                        {option.product_type === ProductType.BUNDLE && (
                          <Chip label="Bundle" size="small" color="secondary" sx={{ height: 20, fontSize: 10 }} />
                        )}
                        <Typography variant="body2" color="primary" fontWeight={500}>
                          {formatCurrency(option.standard_price || 0)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Product" required />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={productQuantity}
                  onChange={(e) => setProductQuantity(parseFloat(e.target.value) || 1)}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Unit Price"
                  type="number"
                  value={productUnitPrice}
                  onChange={(e) => setProductUnitPrice(parseFloat(e.target.value) || 0)}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Discount"
                  type="number"
                  value={productDiscountValue}
                  onChange={(e) => setProductDiscountValue(parseFloat(e.target.value) || 0)}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Discount Type</InputLabel>
                  <Select
                    value={productDiscountType}
                    onChange={(e) => setProductDiscountType(e.target.value)}
                    label="Discount Type"
                  >
                    <MenuItem value="percent">Percentage (%)</MenuItem>
                    <MenuItem value="amount">Fixed Amount ($)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {selectedProduct && (
                <Grid item xs={12}>
                  <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                    <Typography variant="body2">
                      Line Total: <strong>{formatCurrency(
                        calculateLineItemTotal(
                          productQuantity,
                          productUnitPrice || selectedProduct.standard_price || 0,
                          productDiscountType,
                          productDiscountValue
                        ).total_price
                      )}</strong>
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => { setProductDialogOpen(false); resetProductDialog(); }}>Cancel</Button>
            <Button variant="contained" onClick={handleAddProduct} disabled={!selectedProduct}>
              Add Product
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default QuoteBuilder;